import React, { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { KeyRound, ShieldCheck, Download, Upload, Database, MessageCircle } from "lucide-react";

export default function SettingsPage() {
  const { user } = useAuth();
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const fileRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [pendingImport, setPendingImport] = useState(null); // { data, filename }
  const [importing, setImporting] = useState(false);
  const [replaceMode, setReplaceMode] = useState(false);

  const isOwner = user?.role === "admin";
  const [ownerPhone, setOwnerPhone] = useState("");
  const [phoneSaving, setPhoneSaving] = useState(false);

  useEffect(() => {
    if (!isOwner) return;
    api.get("/settings/owner-phone")
      .then(({ data }) => setOwnerPhone(data.phone || ""))
      .catch(() => {});
  }, [isOwner]);

  const savePhone = async (e) => {
    e.preventDefault();
    setPhoneSaving(true);
    try {
      const { data } = await api.put("/settings/owner-phone", { phone: ownerPhone });
      setOwnerPhone(data.phone);
      toast.success("Owner WhatsApp number saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setPhoneSaving(false);
    }
  };

  const BACKUP_COLLECTIONS = [
    "users", "employees", "attendance", "expenses", "payment_requests",
    "inventory_items", "inventory_txns", "recipes", "leave_requests",
    "party_bookings", "daily_sales", "settings",
  ];

  const exportData = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/admin/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `royal-wow-backup-${stamp}.json`;
      a.click();
      URL.revokeObjectURL(url);
      const total = Object.values(data.counts || {}).reduce((s, n) => s + n, 0);
      toast.success(`Exported ${total} rows across ${Object.keys(data.counts || {}).length} collections`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const looksValid = BACKUP_COLLECTIONS.some((c) => Array.isArray(parsed[c]));
      if (!looksValid) {
        toast.error("File is not a valid Royal Wow backup");
        return;
      }
      setPendingImport({ data: parsed, filename: file.name });
    } catch {
      toast.error("Could not read that file");
    } finally {
      e.target.value = "";
    }
  };

  const confirmImport = async () => {
    if (!pendingImport) return;
    setImporting(true);
    try {
      const body = { replace: replaceMode };
      for (const c of BACKUP_COLLECTIONS) {
        if (Array.isArray(pendingImport.data[c])) body[c] = pendingImport.data[c];
      }
      const { data } = await api.post("/admin/import", body);
      const totals = Object.values(data.stats || {}).reduce(
        (acc, s) => ({ added: acc.added + (s.added || 0), skipped: acc.skipped + (s.skipped || 0) }),
        { added: 0, skipped: 0 }
      );
      toast.success(
        `Restored ${totals.added} rows${totals.skipped ? ` (${totals.skipped} skipped as duplicates)` : ""}`
      );
      setPendingImport(null);
      setReplaceMode(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error("New passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: cur,
        new_password: next,
      });
      toast.success("Password updated");
      setCur("");
      setNext("");
      setConfirm("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="settings-page">
      <div className="fade-up">
        <p className="text-xs tracking-[0.3em] uppercase text-muted-foreground">Account</p>
        <h1 className="font-serif text-3xl sm:text-5xl tracking-tight mt-2">Settings</h1>
        <p className="mt-3 text-muted-foreground">Manage your manager account details.</p>
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile card */}
        <div className="bg-white border border-border p-8 fade-up">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-4 h-4 text-primary" aria-hidden="true" />
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Profile</span>
          </div>
          <div className="mt-6">
            <div className="text-xs text-muted-foreground uppercase tracking-widest">Name</div>
            <div className="mt-1 font-serif text-xl" data-testid="settings-name">{user?.name}</div>
          </div>
          <div className="mt-6">
            <div className="text-xs text-muted-foreground uppercase tracking-widest">Email</div>
            <div className="mt-1 font-medium" data-testid="settings-email">{user?.email}</div>
          </div>
          <div className="mt-6">
            <div className="text-xs text-muted-foreground uppercase tracking-widest">Role</div>
            <div className="mt-1 capitalize">{user?.role}</div>
          </div>
        </div>

        {/* Change password */}
        <div className="lg:col-span-2 bg-white border border-border p-8 fade-up">
          <div className="flex items-center gap-3">
            <KeyRound className="w-4 h-4 text-primary" aria-hidden="true" />
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Security</span>
          </div>
          <h2 className="mt-4 font-serif text-2xl tracking-tight">Change password</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Choose a strong password with at least 6 characters.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-6 max-w-xl">
            <div>
              <Label htmlFor="cur" className="text-xs uppercase tracking-widest text-muted-foreground">
                Current password
              </Label>
              <Input
                id="cur"
                data-testid="settings-current-password"
                type="password"
                required
                value={cur}
                onChange={(e) => setCur(e.target.value)}
                className="mt-2 h-12"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="next" className="text-xs uppercase tracking-widest text-muted-foreground">
                  New password
                </Label>
                <Input
                  id="next"
                  data-testid="settings-new-password"
                  type="password"
                  required
                  minLength={6}
                  value={next}
                  onChange={(e) => setNext(e.target.value)}
                  className="mt-2 h-12"
                />
              </div>
              <div>
                <Label htmlFor="confirm" className="text-xs uppercase tracking-widest text-muted-foreground">
                  Confirm new password
                </Label>
                <Input
                  id="confirm"
                  data-testid="settings-confirm-password"
                  type="password"
                  required
                  minLength={6}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="mt-2 h-12"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={busy}
              data-testid="settings-change-password-btn"
              className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {busy ? "Updating…" : "Update password"}
            </Button>
          </form>
        </div>
      </div>

      {/* Owner WhatsApp phone */}
      {isOwner && (
        <div className="mt-6 bg-white border border-border p-8 fade-up" data-testid="owner-phone-panel">
          <div className="flex items-center gap-3">
            <MessageCircle className="w-4 h-4 text-primary" aria-hidden="true" />
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Notifications</span>
          </div>
          <h2 className="mt-4 font-serif text-2xl tracking-tight">Owner WhatsApp number</h2>
          <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
            Used by "Notify Owner on WhatsApp" buttons across the kiosk, payment requests, and the absentee alert.
            Include the country code (no +). Example: <code>919999999999</code>
          </p>
          <form onSubmit={savePhone} className="mt-6 flex items-end gap-3 flex-wrap max-w-xl">
            <div className="flex-1 min-w-[220px]">
              <Label htmlFor="ownerPhone" className="text-xs uppercase tracking-widest text-muted-foreground">
                Phone (with country code)
              </Label>
              <Input
                id="ownerPhone"
                data-testid="owner-phone-input"
                inputMode="tel"
                value={ownerPhone}
                onChange={(e) => setOwnerPhone(e.target.value)}
                placeholder="919999999999"
                className="mt-2 h-12"
              />
            </div>
            <Button
              type="submit"
              disabled={phoneSaving}
              data-testid="save-owner-phone-btn"
              className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {phoneSaving ? "Saving…" : "Save number"}
            </Button>
          </form>
        </div>
      )}

      {/* Owner-only: Full data backup & restore for migrating to a deployed URL */}
      {isOwner && (
        <div className="mt-6 bg-white border border-border p-8 fade-up" data-testid="backup-panel">
          <div className="flex items-center gap-3">
            <Database className="w-4 h-4 text-primary" aria-hidden="true" />
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Data</span>
          </div>
          <h2 className="mt-4 font-serif text-2xl tracking-tight">Backup &amp; restore</h2>
          <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
            Downloads a single JSON with every employee, punch, expense, payment, inventory item, recipe,
            leave, party booking, sale, user account and setting. Use this to migrate from the preview URL
            to your deployed live URL — download here, then upload it on the deployed app.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Button
              type="button"
              onClick={exportData}
              disabled={exporting}
              data-testid="backup-download-btn"
              variant="outline"
              className="rounded-full"
            >
              <Download className="w-4 h-4 mr-2" aria-hidden="true" />
              {exporting ? "Preparing…" : "Download full backup"}
            </Button>

            <Button
              type="button"
              onClick={() => fileRef.current?.click()}
              data-testid="backup-upload-btn"
              className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Upload className="w-4 h-4 mr-2" aria-hidden="true" />
              Upload backup file
            </Button>

            <input
              ref={fileRef}
              type="file"
              accept="application/json,.json"
              className="hidden"
              onChange={onFilePicked}
              data-testid="backup-file-input"
            />
          </div>

          <p className="mt-4 text-xs text-muted-foreground max-w-2xl">
            Tip: on your deployed live URL, sign in as the owner, tap "Upload backup file", pick the JSON
            you downloaded from preview, and tick the wipe-first option in the confirmation dialog. That will
            replace the empty deployed database with your real data in one shot.
          </p>
        </div>
      )}

      {/* Restore confirmation dialog */}
      <AlertDialog open={!!pendingImport} onOpenChange={(v) => { if (!v) setPendingImport(null); }}>
        <AlertDialogContent data-testid="backup-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">Restore this backup?</AlertDialogTitle>
            <AlertDialogDescription>
              File: <code className="text-xs">{pendingImport?.filename}</code>
              <br /><br />
              {pendingImport && (
                <span className="text-xs">
                  Contains:{" "}
                  {["users","employees","attendance","expenses","payment_requests","inventory_items","inventory_txns","recipes","leave_requests","party_bookings","daily_sales","settings"]
                    .filter((c) => Array.isArray(pendingImport.data[c]) && pendingImport.data[c].length)
                    .map((c) => `${pendingImport.data[c].length} ${c}`)
                    .join(" · ")}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <label className="flex items-start gap-3 mt-2 p-3 border border-border bg-secondary/40 cursor-pointer">
            <input
              type="checkbox"
              checked={replaceMode}
              onChange={(e) => setReplaceMode(e.target.checked)}
              data-testid="backup-replace-checkbox"
              className="mt-1"
            />
            <span className="text-sm">
              <strong>Wipe existing data first</strong> — recommended when migrating to a fresh deployed app.
              Without this, existing rows are kept and only new ones are added.
            </span>
          </label>

          <AlertDialogFooter>
            <AlertDialogCancel data-testid="backup-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmImport}
              disabled={importing}
              data-testid="backup-confirm-btn"
              className="bg-primary hover:bg-primary/90"
            >
              {importing ? "Restoring…" : replaceMode ? "Wipe & restore" : "Restore"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
